import { useCallback } from 'react';
import { debounce, scope } from '~/modules/common/debounce';
import {
  getTransitionUrl,
  formatPutBillInput
} from '~/modules/billing-invoicing/common/util';
import {
  getValidationErrorByUri,
  getValidationErrorByDisplayText
} from '~/modules/common/graphql/errors';

const calculationStatusScope = scope();

export const setDisplayIdError = ({
  error,
  setFieldError,
  setCreateAndView,
  setSaving
}) => {
  setCreateAndView(false);
  setSaving(false);
  const displayIdError =
    getValidationErrorByUri(
      error,
      'urn:replicon:validation-failure:invoice-number-is-used-by-another-invoice'
    ) ||
    getValidationErrorByDisplayText(
      error,
      'The specified Invoice already exists.'
    );

  if (displayIdError) {
    setFieldError('displayId', 'duplicateReferenceNumber');
  }
};

export const useAddLineItemsHooks = ({
  billDetails,
  billId,
  billingItemSearch,
  columns,
  createAndView,
  createBill,
  createInvoiceFromBillItemsBatch,
  createInvoiceItemsFromBillingItemsBatch,
  fetchBatchResults,
  fetchCalculationStatus,
  history,
  onClose,
  params,
  putBill,
  refetchAvailableToBillRows,
  refetchAvailableToBillTotal,
  refetchFilteredTotals,
  selectedBillingItems,
  setBillingBatchState,
  setCreateAndView,
  setFieldError,
  setIsBillDataReflectedInAvailableToBillData,
  setSaving,
  setSummarizeColumOptions,
  validateForm,
  values
}) => {
  const onAddLineItems = useCallback(
    async viewBill => {
      if (createBill) {
        const errors = await validateForm();

        if (Object.keys(errors).length) return;
        const {
          displayId,
          description,
          billingAddress,
          issueDate,
          paymentTerms,
          poNumber,
          period,
          dueDate,
          client: { id: clientUri },
          billCurrency: { id: currencyUri },
          invoiceTemplate,
          notesForCustomer,
          internalNotes,
          showComments,
          taxProfile
        } = values;

        if (viewBill) {
          setCreateAndView(true);
        } else setSaving(true);

        try {
          await createInvoiceFromBillItemsBatch({
            billInput: {
              id: null,
              displayId,
              billingAddress,
              description,
              issueDate,
              paymentTerms,
              poNumber,
              period,
              dueDate,
              clientUri,
              currencyUri,
              taxProfileUri: taxProfile && taxProfile.id,
              invoiceTemplateUri: invoiceTemplate && invoiceTemplate.id,
              lineItems: [],
              summarizeColumn: columns,
              notesForCustomer,
              internalNotes,
              showComments
            },
            billingItemColumnOption: columns,
            billingItemSearch,
            selectedBillingItems
          });
        } catch (error) {
          setDisplayIdError({
            error,
            setFieldError,
            setCreateAndView,
            setSaving
          });
        }
      } else {
        setSummarizeColumOptions(columns);
        await createInvoiceItemsFromBillingItemsBatch({
          billId,
          billingItemColumnOption: columns,
          billingItemSearch,
          selectedBillingItems
        });
        const formattedPutBillInput = formatPutBillInput(billDetails);

        formattedPutBillInput.summarizeColumn = columns;
        await putBill(formattedPutBillInput);
      }
    },
    [
      createBill,
      validateForm,
      values,
      setSaving,
      setCreateAndView,
      createInvoiceFromBillItemsBatch,
      columns,
      billingItemSearch,
      selectedBillingItems,
      setFieldError,
      setSummarizeColumOptions,
      createInvoiceItemsFromBillingItemsBatch,
      billId,
      billDetails,
      putBill
    ]
  );

  const refetchAvailableToBillData = useCallback(async () => {
    await Promise.all([
      refetchAvailableToBillRows?.(),
      refetchAvailableToBillTotal?.(),
      refetchFilteredTotals?.()
    ]);
  }, [
    refetchAvailableToBillRows,
    refetchAvailableToBillTotal,
    refetchFilteredTotals
  ]);

  const onCalculationComplete = useCallback(
    async ({ bill, viewBill }) => {
      const response = await fetchCalculationStatus({ billId: bill });
      const status = response?.data?.isBillDataReflectedInAvailableToBillData;

      if (status || !bill) {
        await refetchAvailableToBillData();
        setSaving(false);
        setCreateAndView(false);
        setIsBillDataReflectedInAvailableToBillData(false);

        onClose();
        if (viewBill) {
          const record = { id: bill, type: 'BILL' };
          const path = history.location.pathname;
          const transitionUrl = getTransitionUrl({
            path,
            record,
            slug: params.slug
          });

          history.push(transitionUrl);
        }
      } else {
        await debounce(
          calculationStatusScope,
          async () => {
            await onCalculationComplete({ bill, viewBill });
          },
          500
        );
      }
    },
    [
      fetchCalculationStatus,
      refetchAvailableToBillData,
      setSaving,
      setCreateAndView,
      setIsBillDataReflectedInAvailableToBillData,
      onClose,
      history,
      params
    ]
  );

  const onBatchComplete2 = useCallback(
    async ({ batchId = {} }) => {
      if (createBill && refetchAvailableToBillRows) {
        const result = await fetchBatchResults({ batchId });

        const {
          data: {
            getCreateBillFromBillingItemsBatchResults: { billId: bill } = {}
          } = {}
        } = result;

        setIsBillDataReflectedInAvailableToBillData(true);
        setBillingBatchState({
          batchId: null,
          batchInProgress: false
        });
        await onCalculationComplete({ bill, viewBill: createAndView });

        return undefined;
      }

      const timeoutId = setTimeout(() => {
        onClose();
        setBillingBatchState({
          batchId: null,
          batchInProgress: false
        });
      }, 500);

      return () => clearTimeout(timeoutId);
    },
    [
      createBill,
      refetchAvailableToBillRows,
      fetchBatchResults,
      setIsBillDataReflectedInAvailableToBillData,
      setBillingBatchState,
      onCalculationComplete,
      createAndView,
      onClose
    ]
  );

  return {
    onAddLineItems,
    onBatchComplete2
  };
};

export default useAddLineItemsHooks;
